import React, { useCallback, useEffect, useState } from 'react';
import { api, getCode, rememberCode, forgetCode } from './api.js';
import NewSetForm from './NewSetForm.jsx';
import SetTable from './SetTable.jsx';
import CrossSetPanel from './CrossSetPanel.jsx';

export default function SetListPage() {
  const [sets, setSets] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [creating, setCreating] = useState(false);
  // The code that is actually revealing sets right now, kept so the cross-set panel can ask for
  // the roll-up. It tracks `unlocked` rather than storage, for the same reason `unlocked` does.
  const [activeCode, setActiveCode] = useState('');

  const load = useCallback(async code => {
    setStatus('loading');
    try {
      const data = await api.listSets(code);
      setSets(data.sets);
      // "Unlocked" reflects what the server actually returned, never merely that some code is in
      // storage. A stale code would otherwise show the unlocked banner over an empty list — and
      // because the banner replaces the input, leave no way to type the right one.
      setUnlocked(!!code && data.sets.length > 0);
      setActiveCode(code && data.sets.length ? code : '');
      setStatus('ready');
      return data;
    } catch (err) {
      setError(err.message);
      setStatus('error');
      return null;
    }
  }, []);

  useEffect(() => {
    const stored = getCode(null);
    load(stored).then(data => {
      // A saved code that reveals nothing while sets are still hidden is stale — drop it so the
      // input comes back, rather than stranding the page in a locked-but-claiming-unlocked state.
      if (!data || !stored) return;
      if (!data.sets.length) {
        forgetCode(null);
        setError('Your saved admin code no longer works. Enter it again.');
      }
    });
  }, [load]);

  async function handleUnlock(event) {
    event.preventDefault();
    const code = codeInput.trim();
    if (!code) return;
    const data = await load(code);
    if (!data) return;
    if (data.sets.length) {
      rememberCode(null, code);
      setCodeInput('');
      setError('');
    } else {
      setError('No sets are administered by that code.');
    }
  }

  function handleLock() {
    forgetCode(null);
    setError('');
    load('');
  }

  if (status === 'loading' && !sets.length) return <p className="pred-loading">Loading…</p>;

  return (
    <>
      <div className="pred-page-head">
        <div>
          <h1>Personal Predictions</h1>
          <p className="pred-lede">
            Each set is a list of yes/no predictions, each with a probability, graded after the
            fact. Every set gets a Brier score and a calibration chart. <a href="/about.html">How it works</a>.
          </p>
        </div>
        <button className="pred-btn pred-btn-primary" onClick={() => setCreating(true)}>
          New set
        </button>
      </div>

      <form className="pred-unlock" onSubmit={handleUnlock}>
        {unlocked ? (
          <>
            <span className="pred-unlock-status">
              🔓 Showing the {sets.length} set{sets.length === 1 ? '' : 's'} for this admin code.
            </span>
            <button type="button" className="pred-btn pred-btn-quiet" onClick={handleLock}>
              Show public sets instead
            </button>
          </>
        ) : (
          <>
            <label htmlFor="pred-code">Admin code</label>
            <input
              id="pred-code"
              type="password"
              value={codeInput}
              placeholder="admin code"
              onChange={e => setCodeInput(e.target.value)}
            />
            <button type="submit" className="pred-btn">
              Unlock
            </button>
          </>
        )}
      </form>

      {error && <p className="pred-error">{error}</p>}

      {sets.length ? (
        <SetTable sets={sets} />
      ) : (
        <p className="pred-empty">
          No sets to show. Enter an admin code above to see private sets, or create a new set.
        </p>
      )}

      {unlocked && activeCode && <CrossSetPanel code={activeCode} />}

      {creating && (
        <NewSetForm
          onClose={() => setCreating(false)}
          onCreated={slug => {
            window.location.href = `/set.html?slug=${encodeURIComponent(slug)}`;
          }}
        />
      )}
    </>
  );
}
